/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Trust-badge module rendered below the Order Summary card on the cart page.
 */
export function SecureCheckoutFooter(): ReactElement {
    const { t } = useTranslation('cart');

    return (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid="sf-secure-checkout-footer">
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                <LockKeyhole className="size-7 text-[#111111] stroke-[1.5]" aria-hidden />
            </div>
            <div className="flex flex-col gap-0.5">
                <p className="text-sm font-semibold text-[#111111] leading-tight">
                    {t('secureCheckout.heading')}
                </p>
                <p className="text-xs text-[#4B5563] font-normal leading-normal">
                    {t('secureCheckout.message')}
                </p>
            </div>
        </div>
    );
}
